import LegalLayout, {
  LegalSection,
  LegalParagraph,
  LegalList,
  type UnsfNavbarProps,
  type UnsfFooterProps,
} from "@/components/unsf/LegalLayout"
import type { UnsfMeta } from "@/components/unsf/UnsfHead"

interface Props {
  meta: UnsfMeta
  title: string
  last_updated: string
  intro_section: { title: string; text: string }
  data_section: { title: string; intro: string; items: string[] }
  purpose_section: { title: string; items: string[] }
  legal_basis_section: { title: string; text: string }
  retention_section: {
    title: string
    rows: Array<{ label: string; value: string }>
  }
  rights_section: {
    title: string
    intro: string
    items: string[]
    contact_text: string
    contact_email: string
  }
  cookies_section: { title: string; intro: string; items: string[] }
  contact_section: {
    title: string
    intro: string
    email: string
    address_lines: string[]
  }
  cnil_section: {
    title: string
    text: string
    link_label: string
    link_url: string
  }
  navbar: UnsfNavbarProps
  footer: UnsfFooterProps
  csrf_token: string
  cart: import("@/components/unsf/Cart").CartData
}

export default function Confidentiality({
  meta,
  title,
  last_updated,
  intro_section,
  data_section,
  purpose_section,
  legal_basis_section,
  retention_section,
  rights_section,
  cookies_section,
  contact_section,
  cnil_section,
  navbar,
  footer,
  csrf_token,
  cart,
}: Props) {
  return (
    <LegalLayout
      meta={meta}
      title={title}
      intro={last_updated}
      navbar={navbar}
      footer={footer}
      csrfToken={csrf_token}
      cart={cart}
    >
      <LegalSection title={intro_section.title}>
        <LegalParagraph>{intro_section.text}</LegalParagraph>
      </LegalSection>

      <LegalSection title={data_section.title}>
        <p className="text-content mb-4">{data_section.intro}</p>
        <LegalList items={data_section.items} />
      </LegalSection>

      <LegalSection title={purpose_section.title}>
        <LegalList items={purpose_section.items} />
      </LegalSection>

      <LegalSection title={legal_basis_section.title}>
        <LegalParagraph>{legal_basis_section.text}</LegalParagraph>
      </LegalSection>

      <LegalSection title={retention_section.title}>
        <div className="space-y-3 text-content">
          {retention_section.rows.map((row) => (
            <div key={row.label} className="flex items-start gap-3">
              <span className="font-bold">{row.label}</span>
              <span>{row.value}</span>
            </div>
          ))}
        </div>
      </LegalSection>

      <LegalSection title={rights_section.title}>
        <p className="text-content mb-4">{rights_section.intro}</p>
        <LegalList items={rights_section.items} />
        <p className="text-content mt-4">
          {rights_section.contact_text}
          <a
            href={`mailto:${rights_section.contact_email}`}
            className="underline hover:text-contrast"
          >
            {" "}
            {rights_section.contact_email}
          </a>
        </p>
      </LegalSection>

      <LegalSection title={cookies_section.title}>
        <p className="text-content leading-relaxed mb-4">
          {cookies_section.intro}
        </p>
        <LegalList items={cookies_section.items} />
      </LegalSection>

      <LegalSection title={contact_section.title}>
        <p className="text-content mb-3">{contact_section.intro}</p>
        <div className="bg-light rounded-xl p-6">
          <div className="text-content">
            <div>
              <a
                href={`mailto:${contact_section.email}`}
                className="underline hover:text-contrast"
              >
                {contact_section.email}
              </a>
            </div>
            {contact_section.address_lines.map((line, idx) => (
              <div key={idx} className={idx === 0 ? "mt-2" : ""}>
                {line}
              </div>
            ))}
          </div>
        </div>
      </LegalSection>

      <LegalSection title={cnil_section.title} className="mb-0">
        <p className="text-content leading-relaxed">
          {cnil_section.text}
          <a
            href={cnil_section.link_url}
            target="_blank"
            rel="noreferrer"
            className="underline hover:text-contrast"
          >
            {" "}
            {cnil_section.link_label}
          </a>
        </p>
      </LegalSection>
    </LegalLayout>
  )
}
